import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EventRedirect = ({ eventId }) => {
  const navigate = useNavigate();

  // Event data for meta tags
  const eventData = {
    title: 'EL Bethel | On Campus - WORSHIP Night',
    description: 'Join us for an evening of worship, Christmas carols, music, and stories. Experience the joy of Christmas celebration with our ministry community at Dallas Baptist University.',
    image: 'https://customer-assets.emergentagent.com/job_elbethel-worship/artifacts/54dm58vk_file_00000000fb1c71fdafa2f71e86b37bac.png',
    date: '2025-12-11T20:00:00Z',
    venue: 'Rogers Theater, Patty & Bo Pilgrim Chapel, Dallas Baptist University'
  };

  useEffect(() => {
    // Small delay to allow meta tags to be processed by social media crawlers
    const timer = setTimeout(() => {
      navigate(`/events/${eventId}`, { replace: true });
    }, 1000);

    return () => clearTimeout(timer);
  }, [navigate, eventId]);

  return (
    <>
      {/* Dynamic Meta Tags for Social Sharing */}
      <Helmet>
        <title>{eventData.title} | El Bethel Ministries</title>
        <meta name="description" content={eventData.description} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={eventData.title} />
        <meta property="og:description" content={eventData.description} />
        <meta property="og:image" content={eventData.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${window.location.origin}${window.location.pathname}`} />
        <meta property="og:type" content="event" />
        <meta property="og:site_name" content="El Bethel Ministries" />
        
        {/* Event-specific Open Graph Tags */}
        <meta property="event:start_time" content={eventData.date} />
        <meta property="event:location" content={eventData.venue} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={eventData.title} />
        <meta name="twitter:description" content={eventData.description} />
        <meta name="twitter:image" content={eventData.image} />
        
        {/* Additional Event Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": eventData.title,
            "description": eventData.description,
            "startDate": eventData.date,
            "endDate": "2025-12-11T22:00:00Z",
            "location": {
              "@type": "Place",
              "name": "Rogers Theater, Patty & Bo Pilgrim Chapel",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "3000 Mountain Creek Parkway",
                "addressLocality": "Dallas",
                "addressRegion": "TX",
                "postalCode": "75211",
                "addressCountry": "US"
              }
            },
            "image": eventData.image,
            "organizer": {
              "@type": "Organization",
              "name": "El Bethel Ministries",
              "url": "https://elbethel.online"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": `${window.location.origin}/events/${eventId}`
            }
          })}
        </script>
      </Helmet>

      {/* Fallback content while redirecting */}
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-[#f17b21] rounded-full flex items-center justify-center mx-auto mb-6">
            <Calendar className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4 font-display">
            {eventData.title}
          </h2>
          <p className="text-gray-600 mb-6 font-sans">
            December 11, 2025 • 8:00 PM - 10:00 PM
          </p>
          <div className="animate-pulse bg-gray-200 rounded-lg p-4">
            <p className="text-gray-500 font-sans">Loading event details...</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventRedirect;